import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { millAuthAPI } from '../services/millAPI';

interface MillUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  is_mill_worker: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  can_access_admin_portal?: boolean;
  can_access_store_portal?: boolean;
}

interface MillAuthContextType {
  user: MillUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const MillAuthContext = createContext<MillAuthContextType | undefined>(undefined);

export const MillAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<MillUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check auth by calling /api/auth/me/ (cookies sent automatically)
    // Mill uses shared team login, so we use the main auth endpoint
    const checkAuth = async () => {
      // Timeout: if auth check takes >5s, bail to login
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timeout')), 5000)
      );
      try {
        // Auth check via httpOnly cookie — no localStorage needed
        const response = await Promise.race([millAuthAPI.getMe(), timeout]);
        const userData = response.data;

        // Convert to MillUser interface
        const portals = userData.portals || [];
        const isSuperuser = userData.is_superuser === true;

        const millUser: MillUser = {
          id: userData.id,
          username: userData.username || userData.email,
          email: userData.email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          role: userData.role || 'STAFF',
          role_display: userData.role_display || userData.role || 'Staff',
          is_mill_worker: true,
          is_staff: true,
          is_superuser: isSuperuser,
          can_access_admin_portal: isSuperuser || portals.includes('admin'),
          can_access_store_portal: isSuperuser || portals.includes('store'),
        };

        setUser(millUser);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      // Login endpoint sets httpOnly cookies automatically
      const response = await millAuthAPI.login(username, password);
      const { user: userData } = response;

      setUser(userData);
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    // Logout calls backend to clear httpOnly cookie
    await millAuthAPI.logout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  }), [user, loading, login, logout]);

  return (
    <MillAuthContext.Provider value={value}>
      {children}
    </MillAuthContext.Provider>
  );
};

export const useMillAuth = () => {
  const context = useContext(MillAuthContext);
  if (!context) {
    throw new Error('useMillAuth must be used within MillAuthProvider');
  }
  return context;
};
